const CAP5 = {
  id: "fundamentos-blockchain-5",
  slug: "que-significa-el-estandar-erc20",
  title: "¿Qué significa el estándar ERC-20?",
  sections: [
    {
      id: "hero",
      heading: "Definición y Significado del Estándar ERC-20",
      text: `El término **ERC-20** proviene de *Ethereum Request for Comments número 20*, 
una especificación técnica que define cómo debe comportarse un token fungible dentro del ecosistema Ethereum. 
En otras palabras, un token ERC-20 no es simplemente una moneda digital, sino un **contrato inteligente (smart contract)** 
que implementa una interfaz común de funciones y eventos.  
Gracias a esa estandarización, los tokens pueden interactuar sin problemas con otras aplicaciones, 
billeteras, exchanges y protocolos financieros dentro de la red.`
    },
    {
      id: "origen",
      heading: "Origen y Propósito del Estándar",
      text: `En 2015, cuando Ethereum apenas comenzaba a consolidarse como la principal plataforma de contratos inteligentes, 
cada desarrollador que quería crear su propio token debía escribir un contrato completamente personalizado. 
Esto generaba incompatibilidades: algunos tokens no podían transferirse desde ciertas carteras, 
otros no funcionaban en los intercambios descentralizados o carecían de registro de eventos de transferencia.`,
      list: [
        {
          title: "El rol del EIP-20",
          text: `Para resolver ese caos, **Fabian Vogelsteller** propuso un documento técnico llamado *Ethereum Improvement Proposal (EIP) 20*, 
donde describió la interfaz mínima que todo contrato debía implementar.  
Una vez aprobado, este documento se convirtió en el estándar **ERC-20**, 
que garantiza que todos los tokens compartan las mismas funciones esenciales: *transfer*, *balanceOf*, *approve*, *allowance* y *transferFrom*.`
        },
        {
          title: "Motivación principal",
          text: `El objetivo del estándar fue establecer una **base uniforme de comunicación** entre tokens y contratos inteligentes, 
de modo que cualquier aplicación dentro del ecosistema Ethereum pudiera reconocer y manejar esos activos sin necesidad de código adicional.`
        },
        {
          title: "Impacto del estándar",
          text: `A partir del ERC-20, se volvió posible construir un ecosistema de tokens interoperables, 
lo que impulsó el nacimiento de los **Initial Coin Offerings (ICO)**, 
las **Finanzas Descentralizadas (DeFi)** y la expansión de miles de proyectos basados en Ethereum.`
        }
      ]
    },
    {
      id: "estructura",
      heading: "Cómo Funciona un Token ERC-20",
      text: `En su esencia, un token ERC-20 es un contrato inteligente escrito en **Solidity** que define una serie de variables, funciones y eventos.  
Estas funciones controlan la emisión, transferencia y verificación del saldo de cada usuario. 
A diferencia de una criptomoneda independiente con su propia blockchain (como Bitcoin), 
un token ERC-20 depende completamente de la infraestructura de Ethereum para operar.`,
      list: [
        {
          title: "Componentes básicos del contrato",
          text: `1. **Variables públicas** – almacenan el nombre, símbolo y número de decimales del token.  
2. **Mapping balances** – mantiene un registro de cuánto posee cada dirección.  
3. **Funciones públicas** – permiten transferir, aprobar y consultar saldos.  
4. **Eventos** – notifican a la red sobre transferencias o autorizaciones, 
de manera que las interfaces de usuario (como MetaMask o Etherscan) puedan mostrar esos cambios en tiempo real.`
        },
        {
          title: "Interacción con otros contratos",
          text: `Dado que todos los tokens ERC-20 siguen la misma estructura, 
pueden ser utilizados como activos de entrada o salida en cualquier contrato inteligente, 
por ejemplo, en una dApp de préstamos, una casa de intercambio o una plataforma de apuestas.`
        },
        {
          title: "Gas y ejecución",
          text: `Cada operación ejecutada por un token ERC-20 —transferir, aprobar o emitir— 
requiere pagar una comisión denominada **gas**, medida en Ether (ETH). 
Esto garantiza que los recursos de la red sean utilizados de forma segura y eficiente.`
        }
      ]
    },
    {
      id: "caracteristicas",
      heading: "Características Técnicas y Ventajas del ERC-20",
      text: `El estándar ERC-20 no solo define la interoperabilidad, sino que también establece 
una serie de buenas prácticas para la seguridad y consistencia de los tokens. 
Estas características explican por qué sigue siendo el estándar más utilizado en la actualidad.`,
      list: [
        {
          title: "Interoperabilidad universal",
          text: `Cualquier aplicación que entienda el estándar ERC-20 puede interactuar automáticamente con todos los tokens que lo implementan. 
Esto simplifica la integración de billeteras, intercambios y plataformas DeFi.`
        },
        {
          title: "Facilidad de emisión",
          text: `Cualquier desarrollador puede crear un token ERC-20 en cuestión de minutos 
utilizando bibliotecas de código abierto como OpenZeppelin, 
definiendo parámetros como nombre, símbolo, cantidad total y decimales.`
        },
        {
          title: "Trazabilidad y transparencia",
          text: `Cada transferencia, emisión o quema de tokens queda registrada en la blockchain pública de Ethereum, 
permitiendo auditorías y seguimiento completo del historial de transacciones.`
        },
        {
          title: "Compatibilidad con contratos inteligentes",
          text: `Los tokens ERC-20 pueden integrarse en sistemas automáticos de votación, 
mecanismos de gobernanza DAO o protocolos de recompensas sin necesidad de crear nuevas lógicas de validación.`
        }
      ]
    },
    {
      id: "popularidad",
      heading: "Crecimiento y Popularidad del Estándar",
      text: `Desde su adopción oficial, el estándar ERC-20 ha sido implementado por miles de proyectos. 
Hoy representa más del 80 % de los tokens desplegados en la red Ethereum.`,
      list: [
        {
          title: "Datos de crecimiento",
          text: `En 2025 se estima que existen más de **600 000 contratos ERC-20 activos**, 
abarcando desde stablecoins y tokens de gobernanza hasta utilidades en videojuegos, arte digital y metaversos.`
        },
        {
          title: "Principales ejemplos de éxito",
          text: `Entre los tokens ERC-20 más populares se encuentran **USDC**, **LINK**, **AAVE**, **UNI**, **DAI** y **SAND**, 
todos ellos piezas clave de la economía descentralizada moderna.`
        },
        {
          title: "Evolución hacia nuevos estándares",
          text: `El éxito del ERC-20 sirvió como base para desarrollar otros estándares más avanzados:  
- **ERC-721**, para tokens no fungibles (NFT).  
- **ERC-777**, que mejora la gestión de aprobaciones y callbacks.  
- **ERC-1155**, que combina fungibles y no fungibles en un mismo contrato.`
        }
      ]
    },
    {
      id: "conclusion",
      heading: "Conclusión",
      text: `El estándar ERC-20 es el idioma común de los activos digitales en Ethereum. 
Permite crear, transferir e integrar tokens de forma coherente, 
lo que impulsó la expansión del ecosistema DeFi, los juegos blockchain y los mercados de activos digitales.  
Aunque su estructura es sencilla, su impacto ha sido monumental: gracias al ERC-20, 
Ethereum se transformó en la infraestructura más versátil y dinámica de la Web3.`
    }
  ]
};

export default CAP5;
